import React, { useState, useEffect } from 'react';
import {
  BarChart3, PieChart as PieChartIcon, TrendingUp, Users, Clock, AlertTriangle,
  CheckCircle2, Calendar, Briefcase, FileText, DollarSign, Scale, Cake,
  MessageSquare, RefreshCw, Loader2, ArrowUp, ArrowDown, Minus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';
import { tareasDespachoAPI } from '../api';
import { toast } from '../hooks/use-toast';

// Iconos por categoría
const CATEGORY_ICONS = {
  abogados: Briefcase,
  minutas: FileText,
  costas_pagar: DollarSign,
  ejecuciones: Scale,
  pastelitos: Cake
};

// Colores para gráficos
const COLORS = {
  primary: '#3B82F6',
  success: '#10B981',
  warning: '#F97316',
  danger: '#EF4444',
  purple: '#8B5CF6',
  pink: '#EC4899',
  gray: '#6B7280'
};

const PRIORITY_COLORS = {
  baja: '#9CA3AF',
  media: '#3B82F6',
  alta: '#F97316',
  urgente: '#EF4444'
};

const EstadisticasTareas = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const res = await tareasDespachoAPI.getEstadisticasDetalladas();
      setStats(res.data);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar las estadísticas', variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500">No se pudieron cargar las estadísticas</p>
        <Button onClick={() => loadStats()} className="mt-4">Reintentar</Button>
      </div>
    );
  }

  const { resumen, por_categoria, por_prioridad, tendencia_semanal, por_usuario, atencion_cliente } = stats;

  // Datos para gráfico de barras por categoría
  const datosBarras = por_categoria.map(cat => ({
    nombre: cat.nombre.replace('Tareas ', '').replace('Tarea ', ''),
    pendientes: cat.pendientes,
    completadas: cat.completadas,
    total: cat.total
  }));

  // Calcular tasa de completado
  const tasaCompletado = resumen.total_tareas > 0 
    ? Math.round(((resumen.total_tareas - resumen.pendientes) / resumen.total_tareas) * 100) 
    : 0;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-blue-600" />
            Estadísticas de Tareas
          </h1>
          <p className="text-gray-600 mt-1">Panel de control y métricas del despacho</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => loadStats(true)} 
          disabled={refreshing}
        >
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Actualizar
        </Button>
      </div>

      {/* KPIs Principales */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Tareas</p>
                <p className="text-3xl font-bold">{resumen.total_tareas}</p>
              </div>
              <Briefcase className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Pendientes</p>
                <p className="text-3xl font-bold">{resumen.pendientes}</p>
              </div>
              <Clock className="w-10 h-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Completadas (Mes)</p>
                <p className="text-3xl font-bold">{resumen.completadas_mes}</p>
              </div>
              <CheckCircle2 className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Vencidas</p>
                <p className="text-3xl font-bold">{resumen.vencidas}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Urgentes</p>
                <p className="text-3xl font-bold">{resumen.urgentes}</p>
              </div>
              <TrendingUp className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-cyan-100 text-sm">Completadas Hoy</p>
                <p className="text-3xl font-bold">{resumen.completadas_hoy}</p>
              </div>
              <Calendar className="w-10 h-10 text-cyan-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tasa de Completado General */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold">Tasa de Completado General</h3>
              <p className="text-gray-500 text-sm">Porcentaje de tareas completadas sobre el total</p>
            </div>
            <div className="text-right">
              <span className="text-4xl font-bold text-blue-600">{tasaCompletado}%</span>
            </div>
          </div>
          <Progress value={tasaCompletado} className="h-4" />
          <div className="flex justify-between mt-2 text-sm text-gray-500">
            <span>{resumen.total_tareas - resumen.pendientes} completadas</span>
            <span>{resumen.pendientes} pendientes</span>
          </div>
        </CardContent>
      </Card>

      {/* Gráficos principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de Barras - Tareas por Categoría */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Tareas por Categoría
            </CardTitle>
            <CardDescription>Distribución de pendientes y completadas</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={datosBarras} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="nombre" type="category" width={100} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="pendientes" name="Pendientes" fill={COLORS.warning} radius={[0, 4, 4, 0]} />
                <Bar dataKey="completadas" name="Completadas" fill={COLORS.success} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gráfico de Dona - Por Prioridad */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="w-5 h-5" />
              Tareas Pendientes por Prioridad
            </CardTitle>
            <CardDescription>Distribución según nivel de urgencia</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={por_prioridad.filter(p => p.valor > 0)}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="valor"
                  label={({ nombre, valor }) => `${nombre}: ${valor}`}
                >
                  {por_prioridad.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Tendencia Semanal */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Tendencia Últimos 7 Días
          </CardTitle>
          <CardDescription>Tareas creadas vs completadas por día</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={tendencia_semanal}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dia" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="creadas" 
                name="Creadas" 
                stroke={COLORS.primary} 
                fill={COLORS.primary} 
                fillOpacity={0.3}
              />
              <Area 
                type="monotone" 
                dataKey="completadas" 
                name="Completadas" 
                stroke={COLORS.success} 
                fill={COLORS.success} 
                fillOpacity={0.3}
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Segunda fila de cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Detalle por Categoría */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Detalle por Categoría</CardTitle>
            <CardDescription>Estado actual de cada tipo de tarea</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {por_categoria.map((cat) => {
                const Icon = CATEGORY_ICONS[cat.categoria] || Briefcase;
                const porcentaje = cat.total > 0 ? Math.round((cat.completadas / cat.total) * 100) : 0;
                
                return (
                  <div key={cat.categoria} className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${cat.color}20` }}
                        >
                          <Icon className="w-5 h-5" style={{ color: cat.color }} />
                        </div>
                        <div>
                          <h4 className="font-semibold">{cat.nombre}</h4>
                          <p className="text-sm text-gray-500">{cat.total} tareas totales</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold" style={{ color: cat.color }}>{porcentaje}%</span>
                        <p className="text-xs text-gray-500">completado</p>
                      </div>
                    </div>
                    
                    <Progress value={porcentaje} className="h-2 mb-3" />
                    
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        {cat.pendientes} pendientes
                      </Badge>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {cat.completadas} completadas
                      </Badge>
                      {cat.vencidas > 0 && (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          {cat.vencidas} vencidas
                        </Badge>
                      )}
                      {cat.en_fase_a !== undefined && (
                        <>
                          <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
                            Fase A: {cat.en_fase_a}
                          </Badge>
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">
                            Fase B: {cat.en_fase_b}
                          </Badge>
                          <Badge variant="outline" className="bg-green-50 text-green-700">
                            Fase C: {cat.en_fase_c}
                          </Badge>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Columna derecha */}
        <div className="space-y-6">
          {/* Top Usuarios */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Carga por Usuario
              </CardTitle>
              <CardDescription>Top 5 con más tareas pendientes</CardDescription>
            </CardHeader>
            <CardContent>
              {por_usuario.length > 0 ? (
                <div className="space-y-3">
                  {por_usuario.map((user, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 rounded hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-sm">
                          {user.nombre?.charAt(0) || '?'}
                        </div>
                        <span className="font-medium text-sm">{user.nombre}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{user.pendientes}</Badge>
                        {user.urgentes > 0 && (
                          <Badge variant="destructive" className="text-xs">{user.urgentes} urg</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">No hay tareas asignadas</p>
              )}
            </CardContent>
          </Card>

          {/* Atención al Cliente */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-cyan-600" />
                Atención al Cliente
              </CardTitle>
              <CardDescription>Resumen de consultas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total consultas</span>
                  <span className="font-bold text-lg">{atencion_cliente.total || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-amber-600">Pendientes</span>
                  <Badge className="bg-amber-100 text-amber-700">{atencion_cliente.pendientes || 0}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-blue-600">En proceso</span>
                  <Badge className="bg-blue-100 text-blue-700">{atencion_cliente.en_proceso || 0}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-green-600">Resueltas</span>
                  <Badge className="bg-green-100 text-green-700">{atencion_cliente.resueltas || 0}</Badge>
                </div>
                
                {atencion_cliente.por_origen && (
                  <div className="pt-3 border-t mt-3">
                    <p className="text-sm text-gray-500 mb-2">Por origen:</p>
                    <div className="flex gap-2">
                      <Badge variant="outline" className="text-xs">
                        📞 {atencion_cliente.por_origen.telefono || 0}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        📧 {atencion_cliente.por_origen.email || 0}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        💬 {atencion_cliente.por_origen.whatsapp || 0}
                      </Badge>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EstadisticasTareas;
